import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box, Step, StepLabel, Stepper, SxProps, Theme, Typography} from '@mui/material';
import {ArrowForwardIos, DescriptionOutlined} from '@mui/icons-material';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {businessesApi, contractsApi, tariffsApi, usersApi} from 'src/api';
import {useBusinessId, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {store} from 'src/app/store';
import AMPModal from 'src/components/common/AMPModal';
import {ContractStatsStrip} from 'src/components/contracts/ContractStatsStrip';
import {ContractsTable} from 'src/components/contracts/ContractsTable';
import {ContractSortFieldEnum, ContractStatusEnum, ContractStatusManualEnum} from 'src/enums/contracts';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ListOfTariffs} from '../../components/tariffs/ListOfTariffs';
import {CreateContractGeneralForm} from '../../components/forms/CreateContractGeneralForm';
import {ContractManualFormType} from '../../components/forms/formData/createManualContract';
import {TariffsStatusEnum} from '../../enums';
import {UploadContract} from './UploadContract';
import {createContractManuallySteps, initContractGeneral,} from '../../components/forms/formData/common/initialData';
import {
    ContractSearchRequestData,
    CreateManualContractRequest,
    ListParamsType,
} from 'src/types';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {separatorSx, stepIconsSx, stepperSx,} from '../../components/contracts/CreationContract/CreationContract.sx';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {uploadContractSx} from './UploadContract/UploadContract.sx';
import {NotifyError} from 'src/utils/notification';
import {UserTypeEnum} from 'src/enums/common';

type ContractsQueryTrigger = ReturnType<typeof contractsApi.useLazyGetContractsQuery>[0];
type BusinessesQueryTrigger = ReturnType<typeof businessesApi.useLazyGetBusinessesQuery>[0];
type ClientsQueryTrigger = ReturnType<typeof usersApi.useLazyGetUsersQuery>[0];
type CreateContractManuallyQueryTrigger = ReturnType<
    typeof contractsApi.useCreateManualContractMutation
>[0];
type GetUserQueryTrigger = ReturnType<typeof usersApi.useLazyGetUserByIdQuery>[0];

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one contract and coming back restores the view instead of resetting it.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    status: '',
    locationId: 0,
    sortField: ContractSortFieldEnum.CREATED as string,
    sortAsc: false,
};

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

interface DataType {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [index: string]: any;
}

const handleGetContracts =
    (trigger: ContractsQueryTrigger, businessId?: number, companyId?: number) =>
        async (tableData: ContractSearchRequestData, params: ListParamsType) => {
            store.dispatch(preloaderChangeStatus(true));
            const newData: DataType = {
                ...tableData,
                businessId: tableData.businessId ? tableData.businessId : businessId,
                companyId,
            };
            Object.keys(tableData).map((item: string) => {
                !newData[item] && item !== 'sortField' && item !== 'sortAsc' && delete newData[item];
            });
            const data: ContractSearchRequestData = {
                ...newData,
            };
            try {
                const response = await trigger({data, params}).unwrap();
                store.dispatch(preloaderChangeStatus(false));
                return response;
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

const handleGetUserData = async (
    getUserInfo: GetUserQueryTrigger,
    userId: number,
    businessId: number
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await getUserInfo({
            userId,
            businessId,
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const getBusinessesData = async (
    trigger: BusinessesQueryTrigger,
    size: number,
    page: number
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            params: {
                size,
                page,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const getClientsData = async (
    trigger: ClientsQueryTrigger,
    authBusinessId: number | undefined,
    page: number,
    size: number,
    keyword?: string
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        await trigger({
            data: {
                businessId: authBusinessId,
                contractExists: false,
                userTypes: [UserTypeEnum.PRIVATE_DRIVER],
                keyword,
            },
            params: {
                size,
                page,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

const handleCreateContract = (
    activeTariff: number,
    authBusinessId: number,
    generalInfo: ContractManualFormType,
    createContract: CreateContractManuallyQueryTrigger,
    changeStep: (id: number) => () => void
) => {
    return async () => {
        const data: CreateManualContractRequest = {
            tariffId: activeTariff,
            businessId: authBusinessId,
            contractCreatedAt: generalInfo?.contractCreatedAt,
            signedAt: generalInfo?.contractCreatedAt,
            operationalAt:
                generalInfo?.contractStatus === ContractStatusManualEnum.OPERATION
                    ? (generalInfo?.operationalAt ?? null)
                    : null,
            contractId: generalInfo?.contractId,
            contractStatus: generalInfo?.contractStatus,
            userId: +generalInfo?.userId,
        };
        try {
            store.dispatch(preloaderChangeStatus(true));
            await createContract(data);
            changeStep(2)();
        } catch (e) {
            console.log(e);
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    };
};

export const Contracts: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);

    const [trigger, response] = contractsApi.useLazyGetContractsQuery();
    const [getTariffs, tariffs] = tariffsApi.useLazyGetTariffsQuery();
    const [getUserInfo, userInfo] = usersApi.useLazyGetUserByIdQuery();
    const [createContract, createContractResponse] = contractsApi.useCreateManualContractMutation();

    const [activeTariff, setActiveTariff] = useState<number>(0);
    const [activeStep, setActiveStep] = useState<number>(0);
    const [generalInfo, setGeneralInfo] = useState<ContractManualFormType>(initContractGeneral);
    const [showAddContractModal, setShowAddContractModal] = useState(false);
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: ContractSearchRequestData = useMemo(() => ({
        businessId: 0,
        keyword: query.keyword,
        status: (query.status || undefined) as ContractStatusEnum | undefined,
        locationId: query.locationId || undefined,
        sortAsc: query.sortAsc,
        sortField: query.sortField,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );
    // The button opens the manual creation flow, so it asks for the permission that flow needs
    // rather than for the neighbouring one that moves a contract into operation.
    const canCreateContractManually = useCan('contracts.createManual');
    const readContracts = useCan('contracts.read');

    const navigate = useNavigate();

    const isRealEstate = useDefineIsRealEstateRole();
    const isCompanyUser = persona.isCompany;

    // Anyone who may not read contracts is sent to their own page rather than to an empty list.
    useEffect(() => {
        if (!readContracts) {
            navigate(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(authInfo.id)]));
        }
    }, [readContracts, navigate, authInfo.id]);

    useEffect(() => {
        if (userInfo.data) {
            getTariffs({
                data: {
                    businessId: businessId,
                    status: TariffsStatusEnum.ACTIVE,
                    country: userInfo.data.parkingAddress?.country,
                    region: userInfo.data.parkingAddress?.region,
                },
            }).then(async (res) => {
                if (!res?.data?.content?.length && userInfo.data) {
                    await getTariffs({
                        data: {
                            businessId: businessId,
                            status: TariffsStatusEnum.ACTIVE,
                            country: userInfo.data.parkingAddress?.country,
                            emptyRegion: true,
                        },
                    });
                }
            });
            setActiveStep(1);
        }
    }, [generalInfo, userInfo]);

    const changeStep = (step: number) => () => setActiveStep(step);

    const submitGeneralForm = async (values: ContractManualFormType) => {
        setGeneralInfo({
            ...values,
        });
        await handleGetUserData(getUserInfo, +values.userId, businessId);
    };

    const handleShowAddContractModal = (status: boolean) => () => {
        setShowAddContractModal(status);
        setActiveStep(0);
        if (!status) {
            setGeneralInfo(initContractGeneral);
            setActiveStep(0);
        }
    };

    // The summary the AI agent's own tool already computes, asked with the list's filters minus the
    // one the tiles set: status is the breakdown, so honouring it would leave each tile showing
    // itself. The endpoint is the existing /contracts/summary — the reports page reads it too.
    const {data: contractStats} = contractsApi.useGetContractsSummaryQuery({
        businessId: filterParams.businessId || businessId,
        // The same narrowing the list sends. The server clamps it too, but the block must not depend
        // on that alone: it is the one thing standing between a company and somebody else's totals.
        companyId: authInfo?.company?.id,
        keyword: filterParams.keyword || undefined,
        locationId: filterParams.locationId,
    });

    const handleSelectStatus = (status: ContractStatusEnum) => {
        setQuery({
            ...query,
            page: 0,
            status: query.status === status ? '' : status,
        });
    };

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one.
     */
    const handleChange = (value: ContractSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            status: String(value.status ?? ''),
            locationId: value.locationId ?? 0,
            sortField: value.sortField ?? ContractSortFieldEnum.CREATED,
            sortAsc: value.sortAsc ?? false,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleSelectCard = (id: number) => {
        setActiveTariff(id);
    };

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable, and the business or the
    // company only changes when the session does, which has to re-ask anyway.
    const callHandler = useMemo(
        () => handleGetContracts(trigger, businessId, authInfo?.company?.id),
        [trigger, businessId, authInfo?.company?.id]
    );

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler below only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from `location.state` and a regex over
     * `location.search` - so arriving with a filter already in the URL, which is what Back and a
     * reload both are, fetched the unfiltered list.
     */
    useEffect(() => {
        if (!isSignedIn || !readContracts) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, readContracts, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            <ContractStatsStrip
                data={contractStats}
                activeStatus={filterParams.status}
                onSelectStatus={handleSelectStatus}
            />
            {response.data && (
                <ContractsTable
                    businessId={businessId}
                    filters={filterParams}
                    pageNumber={tableParams.page}
                    data={response.data}
                    size={tableParams.size}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    title={intl.formatMessage({id: 'contracts'})}
                    buttonText="addContract"
                    onAddClick={handleShowAddContractModal(true)}
                    onlyTitle={!canCreateContractManually}
                    icon={<DescriptionOutlined/>}
                />
            )}
            <AMPModal
                handleClose={handleShowAddContractModal(false)}
                open={showAddContractModal}
                title={intl.formatMessage({id: 'createNewContract'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <Stepper
                    activeStep={activeStep}
                    sx={stepperSx}
                    connector={<ArrowForwardIos sx={separatorSx}/>}
                >
                    {createContractManuallySteps(intl).map((label) => (
                        <Step
                            sx={uploadContractSx.stepRoot}
                            key={label.title}
                        >
                            <StepLabel
                                sx={uploadContractSx.stepItem}
                                StepIconProps={{
                                    sx: stepIconsSx,
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    lineHeight="1.2"
                                    sx={uploadContractSx.stepTitle}
                                >
                                    {label.title}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={uploadContractSx.stepDescription}
                                >
                                    {label.description}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {activeStep === 0 && (
                    <CreateContractGeneralForm
                        initialValues={generalInfo}
                        handleSubmit={submitGeneralForm}
                        handleClose={handleShowAddContractModal(false)}
                        businessId={businessId}
                    />
                )}
                {activeStep === 1 && tariffs.data && (
                    <ListOfTariffs
                        activeItem={activeTariff}
                        submitTitle="createContract"
                        content={tariffs.data.content}
                        handleSelectCard={handleSelectCard}
                        changeStep={handleCreateContract(
                            activeTariff,
                            businessId,
                            generalInfo,
                            createContract,
                            changeStep
                        )}
                        handleClose={handleShowAddContractModal(false)}
                        handleBack={changeStep(0)}
                        disableSubmit={activeTariff === 0}
                    />
                )}
                {activeStep === 2 && (
                    <UploadContract
                        handleClose={handleShowAddContractModal(false)}
                        handleBack={changeStep(1)}
                        contractId={createContractResponse?.data?.id}
                        businessId={businessId}
                    />
                )}
            </AMPModal>
        </Box>
    );
};
